import React, { useState, useEffect } from 'react'
import { XMarkIcon, PhotoIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'

const VehicleForm = ({ onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('dados')
  const [vehicleTypes, setVehicleTypes] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [formData, setFormData] = useState({
    type: '',
    vehicle_category: '',
    category: '',
    description: '',
    model: '',
    brand: '',
    ano_modelo: '',  // Preenchido automaticamente pelo OCR (se não capturar, usuário preenche)
    ano_fabricacao: '',  // Preenchido automaticamente pelo OCR (se não capturar, usuário preenche)
    license_plate: '',
    chassis: '',
    renavam: '',
    color: '',
    engine_power: '',
    engine_number: '',
    fuel_type: 'diesel',
    capacidade_carga: '',
    valor_franquia_seguro: '',
    valor_pneu_novo: '',
    percentual_depreciacao_pneu_por_mm: '',
    monthly_rate: '',
    status: 'available'
  })

  const [images, setImages] = useState([])
  const [previews, setPreviews] = useState([])
  const [documentImages, setDocumentImages] = useState([])
  const [documentPreviews, setDocumentPreviews] = useState([])
  const [ocrProcessing, setOcrProcessing] = useState(false)
  const [loading, setLoading] = useState(false)

  const tabs = [
    { id: 'dados', label: '📋 Dados do Veículo' },
    { id: 'valores', label: '💰 Valores' },
    { id: 'fotos', label: '📷 Fotos' },
    { id: 'documentos', label: '📄 Documentos' }
  ]

  // Carregar tipos de veículos da API
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingTypes(true)
        const response = await api.get('/api/vehicle-types', {
          params: { active: 'true' }
        })

        if (response.data && response.data.data) {
          // Converter para formato do select
          const types = response.data.data.map(type => ({
            value: type.name,
            label: type.display_name,
            icon: type.icon
          }))
          setVehicleTypes(types)
        }
      } catch (error) {
        console.error('Erro ao carregar tipos de veículos:', error)
        toast.error('Erro ao carregar tipos de veículos')
        // Fallback para tipos hardcoded em caso de erro
        setVehicleTypes([
          { value: 'truck', label: 'Caminhão', icon: '🚛' },
          { value: 'excavator', label: 'Escavadeira', icon: '🚜' },
          { value: 'crane', label: 'Guindaste', icon: '🏗️' }
        ])
      } finally {
        setLoadingTypes(false)
      }
    }

    fetchVehicleTypes()
  }, [])

  const statusOptions = [
    { value: 'available', label: 'Disponível', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    { value: 'rented', label: 'Alugado', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
    { value: 'maintenance', label: 'Manutenção', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
    { value: 'inactive', label: 'Inativo', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' }
  ]

  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status)
    if (!statusConfig) return null

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    )
  }

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files)

    if (files.length + images.length > 5) {
      toast.error('Máximo 5 imagens por veículo')
      return
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    const invalidFiles = files.filter(file => !validTypes.includes(file.type))

    if (invalidFiles.length > 0) {
      toast.error('Apenas imagens JPG, PNG ou WEBP são permitidas')
      return
    }

    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024)
    if (oversizedFiles.length > 0) {
      toast.error('Imagens devem ter no máximo 5MB')
      return
    }

    const newPreviews = files.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }))

    setImages(prev => [...prev, ...files])
    setPreviews(prev => [...prev, ...newPreviews])
  }

  const removeImage = (index) => {
    URL.revokeObjectURL(previews[index].url)
    setImages(prev => prev.filter((_, i) => i !== index))
    setPreviews(prev => prev.filter((_, i) => i !== index))
  }

  const handleDocumentChange = (e) => {
    const files = Array.from(e.target.files)

    if (files.length + documentImages.length > 10) {
      toast.error('Máximo 10 documentos por veículo')
      return
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
    const invalidFiles = files.filter(file => !validTypes.includes(file.type))

    if (invalidFiles.length > 0) {
      toast.error('Apenas imagens (JPG, PNG, WEBP) ou PDF são permitidos')
      return
    }

    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024)
    if (oversizedFiles.length > 0) {
      toast.error('Documentos devem ter no máximo 10MB')
      return
    }

    const newPreviews = files.map(file => ({
      file,
      url: file.type === 'application/pdf' ? null : URL.createObjectURL(file)
    }))

    setDocumentImages(prev => [...prev, ...files])
    setDocumentPreviews(prev => [...prev, ...newPreviews])
  }

  const removeDocument = (index) => {
    if (documentPreviews[index].url) {
      URL.revokeObjectURL(documentPreviews[index].url)
    }
    setDocumentImages(prev => prev.filter((_, i) => i !== index))
    setDocumentPreviews(prev => prev.filter((_, i) => i !== index))
  }

  // Processar CRLV usando API do backend (MUITO MAIS SIMPLES E CONFIÁVEL!)
  const processCRLV = async () => {
    if (documentImages.length === 0) {
      toast.error('⚠️ Adicione um documento CRLV primeiro')
      return
    }

    const lastDocument = documentImages[documentImages.length - 1]
    console.log('📄 Enviando documento para processamento:', lastDocument.name, lastDocument.type, lastDocument.size, 'bytes')

    setOcrProcessing(true)
    toast.loading('📄 Processando CRLV no servidor...', { id: 'ocr-crlv' })

    try {
      // Criar FormData para enviar o arquivo
      const formData = new FormData()
      formData.append('document', lastDocument)

      // Enviar para o backend
      console.log('🚀 Enviando requisição para /api/ocr/process-crlv')
      const response = await api.post('/api/ocr/process-crlv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      console.log('✅ Resposta recebida:', response.data)

      if (response.data.success && response.data.data) {
        const extractedData = response.data.data
        console.log('✅ Dados extraídos:', extractedData)
        console.log('📄 Texto original (preview):', response.data.rawText)

        // Mapear campos do backend para o formulário
        const mappedData = { ...extractedData }
        if (extractedData.load_capacity) {
          mappedData.capacidade_carga = extractedData.load_capacity
          delete mappedData.load_capacity
        }

        // Preencher os campos do formulário
        setFormData(prev => ({ ...prev, ...mappedData }))

        toast.success(`✅ CRLV processado! ${response.data.fieldsExtracted} campos preenchidos`, { id: 'ocr-crlv' })

        // Mudar para aba Dados para ver os campos preenchidos
        setActiveTab('dados')
      } else {
        toast.error('❌ Nenhum dado foi reconhecido no CRLV', { id: 'ocr-crlv' })
      }
    } catch (error) {
      console.error('❌ Erro ao processar CRLV:', error)

      const errorMsg = error.response?.data?.error || error.message
      const suggestion = error.response?.data?.suggestion

      toast.error(`❌ ${errorMsg}`, { id: 'ocr-crlv', duration: 5000 })

      if (suggestion) {
        console.log('💡 Sugestão:', suggestion)
        toast(suggestion, { icon: '💡', duration: 7000 })
      }
    } finally {
      setOcrProcessing(false)
    }
  }

  const processMulta = async () => {
    if (documentImages.length === 0) {
      toast.error('⚠️ Adicione um documento de multa primeiro')
      return
    }

    let lastDocument = documentImages[documentImages.length - 1]

    // Verificar se é imagem ou PDF
    const isImage = lastDocument.type.startsWith('image/')
    const isPdf = lastDocument.type === 'application/pdf'

    if (!isImage && !isPdf) {
      toast.error('⚠️ O OCR funciona apenas com imagens (JPG, PNG, WEBP) ou PDF')
      return
    }

    setOcrProcessing(true)
    toast.loading('🚨 Lendo dados da multa...', { id: 'ocr-multa' })

    try {
      // Se for PDF, converter para imagem primeiro
      if (isPdf) {
        toast.loading('📄 Convertendo PDF para imagem...', { id: 'ocr-multa' })
        lastDocument = await convertPdfToImage(lastDocument)
        console.log('✅ PDF convertido, iniciando OCR...')
        toast.loading('🚨 Processando OCR da multa...', { id: 'ocr-multa' })
      }

      const { data: { text } } = await Tesseract.recognize(lastDocument, 'por', {
        logger: m => console.log(m)
      })

      console.log('🚨 Texto extraído da multa:', text)

      const multaData = extractMultaData(text)

      if (Object.keys(multaData).length > 0) {
        console.log('📋 Dados da multa extraídos:', multaData)
        toast.success(`✅ Multa processada! Dados: ${JSON.stringify(multaData, null, 2)}`, {
          id: 'ocr-multa',
          duration: 8000
        })
      } else {
        toast.error('❌ Nenhum dado reconhecido na multa', { id: 'ocr-multa' })
      }
    } catch (error) {
      console.error('Erro no OCR da multa:', error)
      toast.error('❌ Erro ao processar multa', { id: 'ocr-multa' })
    } finally {
      setOcrProcessing(false)
    }
  }

  const extractCRLVData = (text) => {
    console.log('🔍 Iniciando extração de dados do CRLV...')
    console.log('📝 Texto recebido (primeiros 500 chars):', text.substring(0, 500))

    const data = {}

    // Placa (ABC-1234 ou ABC1234)
    const placaMatch = text.match(/([A-Z]{3}[-\s]?\d{4})/i)
    console.log('🚗 Regex placa:', placaMatch)
    if (placaMatch) {
      data.license_plate = placaMatch[1].replace(/\s/g, '-').toUpperCase()
      console.log('✅ Placa encontrada:', data.license_plate)
    }

    // RENAVAM (11 dígitos)
    const renavamMatch = text.match(/RENAVAM[:\s]*(\d{11})/i)
    console.log('📋 Regex RENAVAM:', renavamMatch)
    if (renavamMatch) {
      data.renavam = renavamMatch[1]
      console.log('✅ RENAVAM encontrado:', data.renavam)
    }

    // Chassis (17 alfanuméricos)
    const chassisMatch = text.match(/(?:CHASSIS|CHASSI)[:\s]*([A-Z0-9]{17})/i)
    console.log('🔧 Regex chassis:', chassisMatch)
    if (chassisMatch) {
      data.chassis = chassisMatch[1].toUpperCase()
      console.log('✅ Chassis encontrado:', data.chassis)
    }

    // Ano (formato 2020/2021 ou ANO FAB/MODELO)
    const anoMatch = text.match(/(?:ANO.*?)?(\d{4})[\s\/]*(\d{4})/i)
    console.log('📅 Regex ano:', anoMatch)
    if (anoMatch) {
      data.ano_fabricacao = parseInt(anoMatch[1])
      data.ano_modelo = parseInt(anoMatch[2])
      console.log('✅ Anos encontrados:', data.ano_fabricacao, '/', data.ano_modelo)
    }

    // Marca/Fabricante (lista expandida)
    const marcas = [
      'CATERPILLAR', 'CAT', 'KOMATSU', 'VOLVO', 'JOHN DEERE', 'JCB', 'HYUNDAI',
      'SCANIA', 'MERCEDES', 'MERCEDES-BENZ', 'VOLKSWAGEN', 'VW', 'FORD', 'IVECO',
      'DAF', 'MAN', 'RENAULT', 'CASE', 'NEW HOLLAND', 'MASSEY FERGUSON', 'AGRALE'
    ]
    for (const marca of marcas) {
      if (text.toUpperCase().includes(marca)) {
        data.brand = marca
        console.log('✅ Marca encontrada:', data.brand)
        break
      }
    }

    // Modelo (geralmente após marca ou MODELO:)
    const modeloMatch = text.match(/(?:MODELO|MODEL|TIPO)[:\s]*([A-Z0-9\-\s]+?)(?:\n|$|\s{2,})/i)
    console.log('🏷️ Regex modelo:', modeloMatch)
    if (modeloMatch) {
      data.model = modeloMatch[1].trim().toUpperCase()
      console.log('✅ Modelo encontrado:', data.model)
    }

    // Cor (lista expandida)
    const cores = [
      'AMARELO', 'AMARELA', 'BRANCO', 'BRANCA', 'PRETO', 'PRETA', 'AZUL',
      'VERMELHO', 'VERMELHA', 'VERDE', 'LARANJA', 'CINZA', 'PRATA', 'BEGE'
    ]
    for (const cor of cores) {
      if (text.toUpperCase().includes(cor)) {
        data.color = cor.charAt(0) + cor.slice(1).toLowerCase().replace('a', '')
        console.log('✅ Cor encontrada:', data.color)
        break
      }
    }

    console.log('📊 Dados do CRLV extraídos:', data)
    console.log('📊 Total de campos extraídos:', Object.keys(data).length)

    return data
  }

  const extractMultaData = (text) => {
    console.log('🚨 Iniciando extração de dados da multa...')
    console.log('📝 Texto recebido (primeiros 500 chars):', text.substring(0, 500))

    const data = {}

    // Número do Auto de Infração (AIT)
    const aitMatch = text.match(/(?:AIT|AUTO.*?INFRA[ÇC][ÃA]O|N[UÚ]MERO)[:\s]*([A-Z0-9]{8,15})/i)
    console.log('🔢 Regex AIT:', aitMatch)
    if (aitMatch) {
      data.numero_ait = aitMatch[1].toUpperCase()
      console.log('✅ Número AIT encontrado:', data.numero_ait)
    }

    // Placa do veículo
    const placaMatch = text.match(/(?:PLACA|VE[IÍ]CULO)[:\s]*([A-Z]{3}[-\s]?\d{4})/i)
    console.log('🚗 Regex placa:', placaMatch)
    if (placaMatch) {
      data.placa = placaMatch[1].replace(/\s/g, '-').toUpperCase()
      console.log('✅ Placa encontrada:', data.placa)
    }

    // Data da infração (formatos: DD/MM/YYYY ou DD-MM-YYYY)
    const dataMatch = text.match(/(?:DATA.*?INFRA[ÇC][ÃA]O)[:\s]*(\d{2}[\/\-]\d{2}[\/\-]\d{4})/i)
    console.log('📅 Regex data:', dataMatch)
    if (dataMatch) {
      data.data_infracao = dataMatch[1]
      console.log('✅ Data da infração encontrada:', data.data_infracao)
    }

    // Hora da infração (formato: HH:MM)
    const horaMatch = text.match(/(?:HORA)[:\s]*(\d{2}:\d{2})/i)
    console.log('🕐 Regex hora:', horaMatch)
    if (horaMatch) {
      data.hora_infracao = horaMatch[1]
      console.log('✅ Hora da infração encontrada:', data.hora_infracao)
    }

    // Local da infração
    const localMatch = text.match(/(?:LOCAL)[:\s]*([A-Z0-9\s,\.\-]+?)(?:\n|$)/i)
    console.log('📍 Regex local:', localMatch)
    if (localMatch) {
      data.local = localMatch[1].trim()
      console.log('✅ Local encontrado:', data.local)
    }

    // Código da infração (CTB - Código de Trânsito Brasileiro)
    const codigoMatch = text.match(/(?:C[ÓO]DIGO|INFRA[ÇC][ÃA]O)[:\s]*(\d{3,5}[-\s]?\d{0,2})/i)
    console.log('⚠️ Regex código:', codigoMatch)
    if (codigoMatch) {
      data.codigo_infracao = codigoMatch[1]
      console.log('✅ Código da infração encontrado:', data.codigo_infracao)
    }

    // Descrição da infração
    const descricaoMatch = text.match(/(?:INFRA[ÇC][ÃA]O|DESCRI[ÇC][ÃA]O)[:\s]*([A-Z0-9\s,\.\-]+?)(?:\n\n|VALOR|PONTOS)/i)
    console.log('📝 Regex descrição:', descricaoMatch)
    if (descricaoMatch) {
      data.descricao = descricaoMatch[1].trim()
      console.log('✅ Descrição encontrada:', data.descricao)
    }

    // Valor da multa (R$ 123,45)
    const valorMatch = text.match(/(?:VALOR|R\$)[:\s]*R?\$?\s*(\d{1,4}[,\.]\d{2})/i)
    console.log('💰 Regex valor:', valorMatch)
    if (valorMatch) {
      data.valor = valorMatch[1].replace('.', ',')
      console.log('✅ Valor encontrado: R$', data.valor)
    }

    // Pontos na CNH
    const pontosMatch = text.match(/(?:PONTOS|PONTUA[ÇC][ÃA]O)[:\s]*(\d{1,2})/i)
    console.log('🎯 Regex pontos:', pontosMatch)
    if (pontosMatch) {
      data.pontos = parseInt(pontosMatch[1])
      console.log('✅ Pontos encontrados:', data.pontos)
    }

    console.log('📊 Dados da multa extraídos:', data)
    console.log('📊 Total de campos extraídos:', Object.keys(data).length)

    return data
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      console.log('📤 Enviando dados do veículo:', formData)
      console.log('📷 Enviando imagens:', images.length)
      console.log('📄 Enviando documentos:', documentImages.length)

      const submitData = new FormData()

      // Adicionar dados do formulário
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key])
      })

      // Adicionar imagens
      images.forEach(image => {
        submitData.append('images', image)
      })

      // Adicionar documentos
      documentImages.forEach(doc => {
        submitData.append('documents', doc)
      })

      const response = await api.post('/api/vehicles', submitData)
      const result = response.data
      console.log('📋 Resposta do servidor:', result)

      toast.success('✅ Veículo adicionado com sucesso!')
      onSuccess && onSuccess()
      onClose()
    } catch (error) {
      console.error('❌ Erro na requisição:', error)
      const message = error.response?.data?.error || 'Erro ao adicionar veículo'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              🚛 Novo Veículo
            </h2>
            {getStatusBadge(formData.status)}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-1 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Tab 1: Dados do Veículo */}
            {activeTab === 'dados' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Veículo *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    disabled={loadingTypes}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {loadingTypes ? 'Carregando tipos...' : 'Selecione um tipo'}
                    </option>
                    {vehicleTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon ? `${type.icon} ` : ''}{type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Categoria de Vistoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria de Vistoria *
                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400 font-normal">(define o checklist correto)</span>
                  </label>
                  <select
                    name="vehicle_category"
                    value={formData.vehicle_category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione uma categoria</option>
                    <option value="pintura_frio">🎨 Pintura a Frio</option>
                    <option value="munck">🏗️ Munck / Guindaste</option>
                    <option value="bate_estaca">⚙️ Bate-Estaca</option>
                    <option value="carroceria">🚛 Carroceria / Flatbed</option>
                  </select>
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Ex: Caminhão basculante com caçamba reforçada"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Modelo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Modelo *
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: CAT-320"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Marca */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Marca *
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Caterpillar"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Ano Modelo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ano Modelo *
                  </label>
                  <input
                    type="number"
                    name="ano_modelo"
                    value={formData.ano_modelo}
                    onChange={handleInputChange}
                    required
                    min="1980"
                    max={new Date().getFullYear() + 1}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Ano Fabricação */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ano Fabricação *
                  </label>
                  <input
                    type="number"
                    name="ano_fabricacao"
                    value={formData.ano_fabricacao}
                    onChange={handleInputChange}
                    required
                    min="1980"
                    max={new Date().getFullYear() + 1}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Placa */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Placa *
                  </label>
                  <input
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: ABC-1234"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Chassis */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Chassis
                  </label>
                  <input
                    type="text"
                    name="chassis"
                    value={formData.chassis}
                    onChange={handleInputChange}
                    placeholder="Ex: 9BD123456789"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* RENAVAM */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    RENAVAM
                  </label>
                  <input
                    type="text"
                    name="renavam"
                    value={formData.renavam}
                    onChange={handleInputChange}
                    placeholder="Ex: 00123456789"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Cor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cor
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    placeholder="Ex: Amarelo"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Potência do Motor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Potência do Motor
                  </label>
                  <input
                    type="text"
                    name="engine_power"
                    value={formData.engine_power}
                    onChange={handleInputChange}
                    placeholder="Ex: 174 HP"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Número do Motor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Número do Motor
                  </label>
                  <input
                    type="text"
                    name="engine_number"
                    value={formData.engine_number}
                    onChange={handleInputChange}
                    placeholder="Ex: 1234ABC5678"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Tipo de Combustível */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Combustível
                  </label>
                  <select
                    name="fuel_type"
                    value={formData.fuel_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="diesel">Diesel</option>
                    <option value="gasoline">Gasolina</option>
                    <option value="electric">Elétrico</option>
                    <option value="hybrid">Híbrido</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categoria
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder="Ex: ALUGUEL, PARTICULAR, OFICIAL"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Capacidade de Carga */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ⚖️ Capacidade de Carga (kg)
                  </label>
                  <input
                    type="number"
                    name="capacidade_carga"
                    value={formData.capacidade_carga}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 25000"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            {/* Tab 2: Valores */}
            {activeTab === 'valores' && (
              <div className="space-y-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                    ℹ️ Informações sobre os valores
                  </h3>
                  <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                    <li>💰 <strong>Valor Mensal:</strong> Tarifa de locação cobrada mensalmente</li>
                    <li>🛡️ <strong>Franquia de Seguro:</strong> Valor da franquia em caso de sinistro</li>
                    <li>🛞 <strong>Valor do Pneu:</strong> Custo de um pneu novo para o veículo</li>
                    <li>📉 <strong>Depreciação:</strong> Percentual de desgaste do pneu por mm</li>
                    <li>⚖️ <strong>Capacidade:</strong> Capacidade de carga em quilogramas</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Valor Mensal */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      💰 Valor Mensal (R$) *
                    </label>
                    <input
                      type="number"
                      name="monthly_rate"
                      value={formData.monthly_rate}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder="Ex: 10500.00"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Valor Franquia Seguro */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      🛡️ Franquia Seguro (R$)
                    </label>
                    <input
                      type="number"
                      name="valor_franquia_seguro"
                      value={formData.valor_franquia_seguro}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="Ex: 5000.00"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Valor Pneu Novo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      🛞 Valor Pneu Novo (R$)
                    </label>
                    <input
                      type="number"
                      name="valor_pneu_novo"
                      value={formData.valor_pneu_novo}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="Ex: 2500.00"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Percentual Depreciação Pneu */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      📉 Deprec. Pneu por mm (%)
                    </label>
                    <input
                      type="number"
                      name="percentual_depreciacao_pneu_por_mm"
                      value={formData.percentual_depreciacao_pneu_por_mm}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Ex: 5.00"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Fotos */}
            {activeTab === 'fotos' && (
              <div className="space-y-6">
                {/* Upload Area */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    📷 Imagens do Veículo (máximo 5)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
                    <input
                      type="file"
                      id="images"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="images"
                      className="cursor-pointer flex flex-col items-center justify-center"
                    >
                      <PhotoIcon className="w-16 h-16 text-gray-400 mb-3" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Clique para selecionar imagens
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        JPG, PNG ou WEBP - máximo 5MB cada
                      </p>
                    </label>
                  </div>
                </div>

                {/* Previews de Novas Fotos */}
                {previews.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      📸 Novas fotos ({previews.length}/5)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {previews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview.url}
                            alt={`Nova foto ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-green-500 dark:border-green-400"
                          />
                          <span className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-1 rounded">
                            NOVA
                          </span>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {previews.length === 0 && (
                  <div className="text-center py-8">
                    <PhotoIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhuma foto selecionada
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: Documentos */}
            {activeTab === 'documentos' && (
              <div className="space-y-6">
                {/* Botões de OCR */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Botão Ler CRLV */}
                  <button
                    type="button"
                    onClick={processCRLV}
                    disabled={documentImages.length === 0 || ocrProcessing}
                    className={`
                      relative overflow-hidden rounded-lg border-2 p-6 transition-all
                      ${documentImages.length === 0 || ocrProcessing
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-50'
                        : 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-2xl">📄</span>
                        </div>
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          Ler CRLV
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {ocrProcessing ? 'Processando...' : 'Extrair dados do documento do veículo'}
                        </p>
                      </div>
                      {ocrProcessing && (
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Placa</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Chassis</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">RENAVAM</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Ano</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Marca</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Cor</span>
                      </div>
                    </div>
                  </button>

                  {/* Botão Ler Multa */}
                  <button
                    type="button"
                    onClick={processMulta}
                    disabled={documentImages.length === 0 || ocrProcessing}
                    className={`
                      relative overflow-hidden rounded-lg border-2 p-6 transition-all
                      ${documentImages.length === 0 || ocrProcessing
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-50'
                        : 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 cursor-pointer'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-2xl">🚨</span>
                        </div>
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          Ler Multa
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {ocrProcessing ? 'Processando...' : 'Extrair dados do auto de infração'}
                        </p>
                      </div>
                      {ocrProcessing && (
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 border-3 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Nº AIT</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Data</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Local</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Valor</span>
                        <span className="bg-white dark:bg-gray-700 px-2 py-1 rounded">Pontos</span>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Info Box OCR */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                    💡 Como usar o OCR
                  </h3>
                  <ol className="text-sm text-blue-800 dark:text-blue-300 space-y-2 ml-4 list-decimal">
                    <li>Faça upload de um documento <strong>(imagem ou PDF)</strong> usando o botão abaixo</li>
                    <li>Clique em um dos botões acima:
                      <ul className="ml-4 mt-1 space-y-1 list-disc">
                        <li><strong>Ler CRLV</strong>: preenche automaticamente os campos do veículo na aba "Dados"</li>
                        <li><strong>Ler Multa</strong>: extrai informações da multa (exibe no console)</li>
                      </ul>
                    </li>
                    <li>O sistema irá processar o documento (converter PDF se necessário) e extrair os dados automaticamente</li>
                  </ol>
                </div>

                {/* Upload Area */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    📄 Documentos do Veículo
                  </label>
                  <div className="border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-lg p-8 hover:border-purple-400 dark:hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      id="documents"
                      multiple
                      accept="image/*,application/pdf"
                      onChange={handleDocumentChange}
                      className="hidden"
                      disabled={ocrProcessing}
                    />
                    <label
                      htmlFor="documents"
                      className={`cursor-pointer flex flex-col items-center justify-center ${ocrProcessing ? 'opacity-50' : ''}`}
                    >
                      <DocumentArrowUpIcon className={`w-16 h-16 mb-3 ${ocrProcessing ? 'text-purple-400 animate-pulse' : 'text-purple-400'}`} />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {ocrProcessing ? '🔍 Processando OCR...' : 'Clique para fazer upload de documentos'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Imagens ou PDF - máximo 10MB cada
                      </p>
                    </label>
                  </div>
                </div>

                {/* Preview dos Documentos */}
                {documentPreviews.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      📋 Documentos anexados ({documentPreviews.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {documentPreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          {preview.url ? (
                            <img
                              src={preview.url}
                              alt={`Documento ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                            />
                          ) : (
                            <div className="w-full h-32 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                              <DocumentArrowUpIcon className="w-12 h-12 text-gray-400" />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {documentPreviews.length === 0 && (
                  <div className="text-center py-8">
                    <DocumentArrowUpIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhum documento anexado
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {tabs.find(t => t.id === activeTab)?.label}
              </span>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || ocrProcessing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      <span>Salvando...</span>
                    </>
                  ) : ocrProcessing ? (
                    <>
                      <span className="animate-pulse">🔍</span>
                      <span>Processando OCR...</span>
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      <span>Salvar Veículo</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default VehicleForm
